import { onMounted, ref } from 'vue';
import { useGlobalAudioContext } from '@/composables/useGlobalAudioContext';

const showMidiSetupDialog = ref(false);
const globalAudio = useGlobalAudioContext();
let completeMidiSetupPromise: Promise<boolean> | null = null;

const invalidateReadyState = () => {
  showMidiSetupDialog.value = true;
};

const syncDialogWithAudioState = () => {
  showMidiSetupDialog.value = !globalAudio.isAudioRunning();
};

if (typeof window !== 'undefined') {
  // A recreated audio context always requires a fresh user gesture flow.
  globalAudio.onAudioContextRecreated(() => {
    invalidateReadyState();
  });

  window.addEventListener('pageshow', syncDialogWithAudioState, { passive: true });
  window.addEventListener('focus', syncDialogWithAudioState, { passive: true });
}

const completeMidiSetup = async (onError?: () => void) => {
  if (completeMidiSetupPromise) return completeMidiSetupPromise;
  completeMidiSetupPromise = (async () => {
    try {
      await globalAudio.ensureReadyFromUserGesture();
      syncDialogWithAudioState();
      return !showMidiSetupDialog.value;
    } catch {
      onError?.();
      return false;
    } finally {
      completeMidiSetupPromise = null;
    }
  })();
  return completeMidiSetupPromise;
};

const requireReadyOrPrompt = () => {
  syncDialogWithAudioState();
  if (!showMidiSetupDialog.value) {
    return true;
  }
  showMidiSetupDialog.value = true;
  return false;
};

export function useMidiAudioSetup() {
  onMounted(() => {
    syncDialogWithAudioState();
  });

  return {
    showMidiSetupDialog,
    invalidateReadyState,
    completeMidiSetup,
    requireReadyOrPrompt,
  };
}
